import { Ng2StateDeclaration, StatesModule, Transition } from '@uirouter/angular';
import { FiStateNames } from '../shared/fi-state-proxy/fi-state-names';
import { TaskV2NavigationTabsComponent } from './task-v2-navigation-tabs/task-v2-navigation-tabs.component';
import { StateAccessCheckService } from '../shared/fi-shared-services/state-access-check.service';
import { AccessRightType, ApplicationSettingsType, ObjectTypes } from '../shared/types';
import { FiTasksViewMode } from '@fi-shared/fi-task-shared/fi-task-enums';
import { AccessRightService } from '@fi-shared/fi-shared-services';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiCommentsService } from '@fi-shared/fi-comments/fi-comments.service';
import { TaskDetailsCommentIdResolver, taskDetailsState_comments } from '@fi-app/task/task-states';
import { coerceNumberProperty } from '@angular/cdk/coercion';
import { FiTaskV2Service } from '@fi-app/task-v2/fi-task-v2.service';
import { FiTaskV2ListComponent } from '@fi-app/task-v2/fi-task-v2-list/fi-task-v2-list.component';
import { FiTaskV2ArchivedListComponent } from '@fi-app/task-v2/fi-task-v2-archived-list/fi-task-v2-archived-list.component';
import { FiTaskV2EditComponent } from '@fi-app/task-v2/fi-task-v2-edit/fi-task-v2-edit.component';
import { FiTaskV2HelperService } from '@fi-app/task-v2/fi-task-v2-helper.service';
import { FiTaskV2DetailsComponent } from '@fi-app/task-v2/fi-task-v2-details/fi-task-v2-details.component';

const accessRightFilterCollectionResolveFn = [
    AccessRightService,
    (accessRightService: AccessRightService) => {
        return accessRightService.getAccessRightsForPublicationByObjectType(ObjectTypes.Task2);
    },
];

const taskResolveFn = [
    FiStateService,
    Transition,
    FiTaskV2Service,
    (fiStateService: FiStateService, tr: Transition, fiTaskV2Service: FiTaskV2Service) => {
        const taskId = coerceNumberProperty(fiStateService.getParams(tr).id);
        return fiTaskV2Service.getTask(taskId);
    },
];

const taskDetailsResolveFn = [
    FiStateService,
    Transition,
    FiTaskV2Service,
    (fiStateService: FiStateService, tr: Transition, fiTaskV2Service: FiTaskV2Service) => {
        const taskId = coerceNumberProperty(fiStateService.getParams(tr).id);
        return fiTaskV2Service.getTaskDetails(taskId);
    },
];

const TaskV2State: Ng2StateDeclaration = {
    name: FiStateNames.TaskV2._name,
    url: '/Task2',
    abstract: true,
    component: TaskV2NavigationTabsComponent,
};

const TaskV2CurrentState: Ng2StateDeclaration = {
    name: FiStateNames.TaskV2.Current,
    url: '/Current?:filterType&:sortColumn&:sortDirection&:offset',
    component: FiTaskV2ListComponent,
    reloadOnSearch: false,
    resolve: {
        viewMode: [() => FiTasksViewMode.notCompleted],
    },
};

const TaskV2CompletedState: Ng2StateDeclaration = {
    name: FiStateNames.TaskV2.Completed,
    url: '/Completed?:filterType&:sortColumn&:sortDirection&:offset',
    component: FiTaskV2ListComponent,
    reloadOnSearch: false,
    resolve: {
        viewMode: [() => FiTasksViewMode.completed],
    },
};

const TaskV2CancelledState: Ng2StateDeclaration = {
    name: FiStateNames.TaskV2.Cancelled,
    url: '/Cancelled?:filterType&:sortColumn&:sortDirection&:offset',
    component: FiTaskV2ListComponent,
    reloadOnSearch: false,
    resolve: {
        viewMode: [() => FiTasksViewMode.cancelled],
    },
};

const TaskV2ArchivedState: Ng2StateDeclaration = {
    name: FiStateNames.TaskV2.Archived,
    url: '/Archived?:filterType&:sortColumn&:sortDirection&:offset',
    component: FiTaskV2ArchivedListComponent,
    reloadOnSearch: false,
};

const TaskV2AddState: Ng2StateDeclaration = {
    name: FiStateNames.TaskV2.Add,
    url: '/Add',
    views: {
        '@Module': {
            component: FiTaskV2EditComponent,
        },
    },
    resolve: {
        task: [
            FiTaskV2HelperService,
            (fiTaskV2HelperService: FiTaskV2HelperService) => fiTaskV2HelperService.getNewTaskV2Instance(),
        ],
        availableAccessRightFilterCollection: accessRightFilterCollectionResolveFn,
    },
};

const TaskV2DetailsState: Ng2StateDeclaration = {
    name: FiStateNames.TaskV2.Details,
    url: '/Details/{id:[0-9]+}',
    views: {
        '@Module': {
            component: FiTaskV2DetailsComponent,
        },
    },
    params: {
        commentId: undefined,
    },
    resolve: {
        task: taskDetailsResolveFn,
        comments: [FiStateService, Transition, FiCommentsService, taskDetailsState_comments],
        availableAccessRightFilterCollection: accessRightFilterCollectionResolveFn,
        commentId: TaskDetailsCommentIdResolver,
    },
};

const TaskV2EditState: Ng2StateDeclaration = {
    name: FiStateNames.TaskV2.Edit,
    url: '/Edit/{id:[0-9]+}',
    views: {
        '@Module': {
            component: FiTaskV2EditComponent,
        },
    },
    resolve: {
        task: taskResolveFn,
        availableAccessRightFilterCollection: accessRightFilterCollectionResolveFn,
        isEditMode: [() => true],
    },
};

export const TaskV2States: StatesModule = {
    states: [
        TaskV2State,
        TaskV2CurrentState,
        TaskV2CompletedState,
        TaskV2CancelledState,
        TaskV2AddState,
        TaskV2DetailsState,
        TaskV2EditState,
        TaskV2ArchivedState,
    ],
};

export function registerTaskV2StateRules(stateAccessCheckService: StateAccessCheckService) {
    stateAccessCheckService.registerStateRules([FiStateNames.TaskV2.Current, FiStateNames.TaskV2.Details], {
        SettingType: [ApplicationSettingsType.TaskV2Local],
        AccessType: AccessRightType.Read,
    });

    stateAccessCheckService.registerStateRules([FiStateNames.TaskV2.Add], {
        SettingType: [ApplicationSettingsType.TaskV2Local],
        AccessType: AccessRightType.Read,
    });

    stateAccessCheckService.registerStateRules([FiStateNames.TaskV2.Report._name], {
        SettingType: [ApplicationSettingsType.TaskV2Reports],
        AccessType: AccessRightType.Read,
    });
}
